// components/TopBar.js
'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { useI18n } from './i18n'
import LanguageSwitcher from './LanguageSwitcher'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import ViewportAnimatedImage from './visual-runtime/ViewportAnimatedImage'
import { OFFICIAL_QUANTUM_L7_CHANNELS } from '../lib/brand/officialChannels'
import { runAuthorizedClientAction } from '../lib/authActionGateClient'
 
const AuthNavClient = dynamic(() => import('./AuthNavClient'), { ssr: false })

const AUTH_LOCAL_STORAGE_KEYS = [
  'account',
  'wallet',
  'asherId',
  'ql7_uid',
  'ql7_account',
  'forum_user_id',
  'ql7_wallet_address',
  'ql7_wallet_address_lc',
  'ql7_wallet_account_id',
  'ql7_wallet_session_token',
  'ql7_wallet_session_expires_at',
  'ql7_wallet_session_provider',
  'ql7_vip',
  'ai_quota_vip',
  'ql7_admin',
  'w3m-auth-provider',
  'W3M_CONNECTED_CONNECTOR',
  'WEB3_CONNECT_CACHED_PROVIDER',
  'wagmi.store',
  'wagmi.recentConnectorId',
  'wagmi.injected.connected',
]

const AUTH_STORAGE_PREFIXES = [
  'w3m-',
  'W3M_',
  'wc@',
  'walletconnect',
  'WALLETCONNECT',
  '@w3m',
  'appkit',
  'reown',
]

const AUTH_LOGOUT_LOCK_KEY = 'ql7_auth_logout_lock'

const TOPBAR_AUTH_PORTAL_ID = 'ql7-topbar-auth-slot'
const TOPBAR_PORTAL_EVENT = 'ql7:topbar-portal-change'
const TOPBAR_PREWARM_MARGIN_PX = 3200
const TOPBAR_KEEP_MARGIN_PX = 4600
const TOPBAR_PARK_DELAY_MS = 900
const TOPBAR_REMOUNT_GEOMETRY_LOCK_MS = 450
const TOPBAR_SHRINK_CONFIRM_MS = 280
const TOPBAR_HEIGHT_EPSILON_PX = 0.125
const ORBIT_PRESS_DIP_MS = 72
const ORBIT_NAVIGATION_DELAY_MS = 260
const ORBIT_PRESS_MAX_TILT_DEG = 7.5
const ORBIT_HIT_STROKE_VIEWBOX = 84

function getAuthStorage(kind) {
  try {
    if (typeof window === 'undefined') return null
    return kind === 'session' ? window.sessionStorage : window.localStorage
  } catch {
    return null
  }
}

function authStorageSet(kind, key, value) {
  try {
    getAuthStorage(kind)?.setItem(key, value)
  } catch {}
}

function authStorageRemove(kind, key) {
  try {
    getAuthStorage(kind)?.removeItem(key)
  } catch {}
}

function markWagmiDisconnected() {
  try {
    if (typeof window === 'undefined') return
    authStorageSet('local', 'wagmi.injected.disconnected', 'true')
    authStorageSet('session', 'wagmi.injected.disconnected', 'true')
    authStorageRemove('local', 'wagmi.injected.connected')
    authStorageRemove('session', 'wagmi.injected.connected')
    authStorageRemove('local', 'wagmi.recentConnectorId')
    authStorageRemove('session', 'wagmi.recentConnectorId')
  } catch {}
}

function markAuthLogoutLock() {
  try {
    if (typeof window === 'undefined') return
    const value = String(Date.now())
    authStorageSet('local', AUTH_LOGOUT_LOCK_KEY, value)
    authStorageSet('session', AUTH_LOGOUT_LOCK_KEY, value)
    window.__QL7_AUTH_LOGGED_OUT__ = true
  } catch {}
}

function clearCookie(name) {
  try {
    const host = window.location.hostname
    const base = `${encodeURIComponent(name)}=; Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
    document.cookie = base
    if (host && host !== 'localhost' && !/^\d+\.\d+\.\d+\.\d+$/.test(host)) {
      document.cookie = `${base}; domain=${host}`
      document.cookie = `${base}; domain=.${host.replace(/^\./, '')}`
    }
  } catch {}
}

function clearAuthClientState() {
  try {
    if (typeof window === 'undefined') return
    const w = window
    try { delete w.__AUTH_ACCOUNT__ } catch { w.__AUTH_ACCOUNT__ = undefined }
    try { delete w.__ASHER_ID__ } catch { w.__ASHER_ID__ = undefined }
    try { delete w.__QL7_UID__ } catch { w.__QL7_UID__ = undefined }
    try { delete w.__FORUM_USER__ } catch { w.__FORUM_USER__ = undefined }
    try { delete w.wallet } catch { w.wallet = undefined }
    try { delete w.account } catch { w.account = undefined }

    const clearStore = (store) => {
      if (!store) return
      const keys = []
      for (let i = 0; i < store.length; i += 1) {
        const key = store.key(i)
        if (!key) continue
        if (
          AUTH_LOCAL_STORAGE_KEYS.includes(key) ||
          AUTH_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix))
        ) {
          keys.push(key)
        }
      }
      AUTH_LOCAL_STORAGE_KEYS.forEach((key) => keys.push(key))
      Array.from(new Set(keys)).forEach((key) => {
        try { store.removeItem(key) } catch {}
      })
    }

    clearStore(getAuthStorage('local'))
    clearStore(getAuthStorage('session'))
    markWagmiDisconnected()
    ;['asherId', 'account', 'wallet', 'ql7_uid', 'ql7_account', 'forum_user_id'].forEach(clearCookie)
    markAuthLogoutLock()
  } catch {}
}


const METAMARKET_GIFTS_SOURCE = 'metamarket_gifts'
const NOTIFICATION_COUNTS_STORAGE_KEY = 'ql7_notification_counts_v1'

function readMetaMarketGiftNotificationCount(detail = null) {
  try {
    const source = String(detail?.source || '').trim()
    if (source === METAMARKET_GIFTS_SOURCE) {
      return Math.max(0, Number(detail?.count) || 0)
    }
    const stateCount = Number(
      detail?.counts?.[METAMARKET_GIFTS_SOURCE] ??
      window.__QL7_NOTIFICATION_STATE__?.counts?.[METAMARKET_GIFTS_SOURCE] ??
      0,
    )
    if (stateCount > 0) return stateCount
    const storedCounts = JSON.parse(window.localStorage.getItem(NOTIFICATION_COUNTS_STORAGE_KEY) || '{}')
    return Math.max(0, Number(storedCounts?.[METAMARKET_GIFTS_SOURCE]) || 0)
  } catch {
    return 0
  }
}

// математика: точка на окружности по углу
function polarToCartesian (cx, cy, r, angleDeg) {
  const rad = (Math.PI / 180) * (angleDeg - 90)
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  }
}

const SITE_SOCIAL_ICON_BY_ID = Object.freeze({
  'telegram-channel': '/icons/telegram.png',
  linkedin: '/icons/linkedin.png',
  x: '/icons/twitter.png',
  instagram: '/icons/instagram.png',
  tiktok: '/icons/tiktok.png',
  youtube: '/icons/youtube.png',
})

const SITE_SOCIAL_LINKS = Object.freeze(
  OFFICIAL_QUANTUM_L7_CHANNELS
    .filter((item) => item.kind === 'social' && SITE_SOCIAL_ICON_BY_ID[item.id])
    .map((item) => Object.freeze({ ...item, icon: SITE_SOCIAL_ICON_BY_ID[item.id] })),
)

export function SiteSocialLinksRow({ className = '' }) {
  const rowClassName = ['ql7-social-links-row', className].filter(Boolean).join(' ')

  return (
    <nav className={rowClassName} aria-label="Quantum L7 AI official social links" data-ql7-footer-trust-link="absent">
      <span className="ql7-social-links-row__icons">
        {SITE_SOCIAL_LINKS.map((item) => (
          <a
            key={item.id}
            className="ql7-social-link"
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={item.label}
            title={item.label}
          >
            <Image
              className="ql7-social-link__icon"
              src={item.icon}
              alt=""
              width={64}
              height={64}
              draggable={false}
            />
          </a>
        ))}
      </span>
    </nav>
  )
}


function TopBarVisual ({ rootRef }) {
  const { t } = useI18n()
  const pathname = usePathname()
  const router = useRouter()
  const [metaMarketGiftAlert, setMetaMarketGiftAlert] = useState(false)
  const orbitRef = useRef(null)
  const orbitPressStartedAtRef = useRef(0)
  const orbitReleaseTimerRef = useRef(null)
  const orbitNavigateTimerRef = useRef(null)
  const [orbitPress, setOrbitPress] = useState({ active: false, tiltX: 0, tiltY: 0 })

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const notifyPortalChange = () => {
      try { window.dispatchEvent(new Event(TOPBAR_PORTAL_EVENT)) } catch {}
    }
    notifyPortalChange()
    return notifyPortalChange
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const updateGiftAlert = (detail = null) => {
      setMetaMarketGiftAlert(readMetaMarketGiftNotificationCount(detail) > 0)
    }
    const onCount = (event) => {
      const detail = event?.detail || {}
      if (String(detail?.source || '').trim() !== METAMARKET_GIFTS_SOURCE) return
      updateGiftAlert(detail)
    }
    const onState = (event) => updateGiftAlert(event?.detail || null)
    const onStorage = (event) => {
      if (!event || event.key === NOTIFICATION_COUNTS_STORAGE_KEY) updateGiftAlert()
    }

    updateGiftAlert()
    window.addEventListener('ql7:notification-count', onCount)
    window.addEventListener('ql7:notification-state', onState)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener('ql7:notification-count', onCount)
      window.removeEventListener('ql7:notification-state', onState)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  const items = [
    { href: '/exchange',  label: t('nav_exchange') },
    { href: '/academy',   label: t('nav_academy') },  
    { href: '/about',     label: t('nav_about') },
    { href: '/subscribe', label: t('nav_subscribe') }, 
    { href: '/forum',     label: t('forum_title') },    
  ] 
 
  const isActive = (href) => {
    if (href === '/') return pathname === '/'
    return pathname === href || pathname.startsWith(href + '/')
  }

  const activeIndex = Math.max(
    0,
    items.findIndex((item) => isActive(item.href))
  )

  // ===== авто-масштаб под самые длинные лейблы (арабский и т.п.) =========
  const maxLabelLen = items.reduce(
    (max, item) => Math.max(max, (item.label || '').length),
    0
  )

  let orbitScale = 1
  if (maxLabelLen > 28) orbitScale = 0.6
  else if (maxLabelLen > 24) orbitScale = 0.68
  else if (maxLabelLen > 20) orbitScale = 0.78
  else if (maxLabelLen > 16) orbitScale = 0.88

  // ===== Геометрия монеты =================================================
  const VIEWBOX = 400
  const CX = VIEWBOX / 2
  const CY = VIEWBOX / 2
  const R  = 150
  const angleStep = 360 / items.length

  const clearOrbitTimer = (ref) => {
    if (!ref.current) return
    clearTimeout(ref.current)
    ref.current = null
  }

  const releaseOrbitPress = () => {
    setOrbitPress((current) => (
      current.active || current.tiltX !== 0 || current.tiltY !== 0
        ? { active: false, tiltX: 0, tiltY: 0 }
        : current
    ))
  }

  const applyOrbitPress = (rawX, rawY) => {
    const x = Math.max(-1, Math.min(1, Number(rawX) || 0))
    const y = Math.max(-1, Math.min(1, Number(rawY) || 0))

    clearOrbitTimer(orbitReleaseTimerRef)
    clearOrbitTimer(orbitNavigateTimerRef)
    orbitPressStartedAtRef.current = typeof performance !== 'undefined' ? performance.now() : Date.now()
    setOrbitPress({
      active: true,
      tiltX: -y * ORBIT_PRESS_MAX_TILT_DEG,
      tiltY: x * ORBIT_PRESS_MAX_TILT_DEG,
    })

    // Premium impulse: dip toward the touch, then spring back automatically.
    // Release never waits for pointer-up/navigation, so long presses cannot stick.
    orbitReleaseTimerRef.current = setTimeout(() => {
      orbitReleaseTimerRef.current = null
      releaseOrbitPress()
    }, ORBIT_PRESS_DIP_MS)
  }

  const beginOrbitPointerPress = (event) => {
    if (event?.pointerType === 'mouse' && event.button !== 0) return
    const orbit = orbitRef.current
    if (!orbit) return

    const rect = orbit.getBoundingClientRect()
    if (!(rect.width > 0) || !(rect.height > 0)) return

    const x = (Number(event.clientX) - (rect.left + rect.width / 2)) / (rect.width / 2)
    const y = (Number(event.clientY) - (rect.top + rect.height / 2)) / (rect.height / 2)
    applyOrbitPress(x, y)

    try { event.currentTarget?.setPointerCapture?.(event.pointerId) } catch {}
  }

  const beginOrbitKeyboardPress = (index) => {
    const centerDeg = angleStep * (index - activeIndex)
    const point = polarToCartesian(0, 0, 1, centerDeg)
    applyOrbitPress(point.x, point.y)
  }

  const finishOrbitPointerPress = (event) => {
    try { event.currentTarget?.releasePointerCapture?.(event.pointerId) } catch {}
  }

  const navigateOrbit = (href) => {
    const startedAt = Number(orbitPressStartedAtRef.current || 0)
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
    const wait = Math.max(0, ORBIT_NAVIGATION_DELAY_MS - Math.max(0, now - startedAt))

    // Keep the independent spring-back alive. Start route rotation only after
    // the dip-and-return gesture has visually completed.
    clearOrbitTimer(orbitNavigateTimerRef)
    orbitNavigateTimerRef.current = setTimeout(() => {
      orbitNavigateTimerRef.current = null
      router.push(href)
    }, wait)
  }

  useEffect(() => () => {
    if (orbitReleaseTimerRef.current) clearTimeout(orbitReleaseTimerRef.current)
    if (orbitNavigateTimerRef.current) clearTimeout(orbitNavigateTimerRef.current)
    orbitReleaseTimerRef.current = null
    orbitNavigateTimerRef.current = null
  }, [])

  // активный сегмент всегда наверху: центр дуги = -90°
  const baseAngle = 0 - angleStep / 2

  // дуги под textPath
  const arcPaths = items.map((_, index) => {
    // чуть уменьшили зазор между дугами, чтобы «дуга к дуге»
    const paddingDeg = 2
    const startDeg = baseAngle + angleStep * index + paddingDeg
    const endDeg   = baseAngle + angleStep * (index + 1) - paddingDeg

    const start = polarToCartesian(CX, CY, R, startDeg)
    const end   = polarToCartesian(CX, CY, R, endDeg)

    const d = [
      `M ${start.x.toFixed(3)} ${start.y.toFixed(3)}`,
      `A ${R} ${R} 0 0 1 ${end.x.toFixed(3)} ${end.y.toFixed(3)}`
    ].join(' ')

    return { id: `ql7-nav-arc-${index}`, d }
  })

  const openMetaMarketFromBrand = () => {
    if (typeof window === 'undefined') return

    void runAuthorizedClientAction({
      actionKey: 'topbar-metamarket-open',
      source: 'topbar-metamarket',

      action: (accountId) => {
        window.dispatchEvent(new CustomEvent('metamarket:open', {
          detail: {
            source: 'topbar-brand',
            initialMode: metaMarketGiftAlert ? 'collections' : 'market',
            giftFlow: false,
            viewerId: accountId,
          },
        }))
      },
    })
  }

  return (
    <header ref={rootRef} className="topbar" data-ql7-visual-scope="header">
      {/* логотип слева */}
<Link href="/" className="brand" aria-label="Quantum L7 AI">
  <span className="brand-logo-wrap">
    <Image
      src="/branding/quantum_l7_logo.png"
      alt="Quantum L7 AI"
      className="brand-logo"
      width={160}
      height={40}
      priority
    />

    <span
      className="brand-logo-shine"
      aria-hidden="true"
    />
  </span>
</Link>

      {/* колонка справа: орбита + под ней auth+lang */}
      <nav aria-label="Main" className="topbar-nav">
        <div className="nav-orbit-shell">
          <div
            ref={orbitRef}
            className="nav-orbit"
            data-orbit-pressed={orbitPress.active ? 'true' : 'false'}
            style={{
              '--orbit-count': items.length,
              '--orbit-active-index': activeIndex,
              '--orbit-scale': orbitScale,
              '--orbit-press-tilt-x': `${orbitPress.tiltX.toFixed(2)}deg`,
              '--orbit-press-tilt-y': `${orbitPress.tiltY.toFixed(2)}deg`,
            }}
          >
            {/* фоновые бегущие импульсные кольца */}
            <div className="nav-orbit-rings" aria-hidden="true" />

            {/* SVG-монета с текстом по дуге */}
            <svg
              className="nav-orbit-svg"
              viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
              aria-hidden="false"
              role="group"
            >
              <defs>
                {arcPaths.map((arc) => (
                  <path
                    key={arc.id}
                    id={arc.id}
                    d={arc.d}
                    fill="none"
                  />
                ))}

                {/* тёмное «узкое» кольцо-подложка под текстом */}
                <linearGradient id="ql7-orbit-band-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%"   stopColor="#020617" stopOpacity="0.9" />
                  <stop offset="40%"  stopColor="#020617" stopOpacity="0.85" />
                  <stop offset="60%"  stopColor="#020617" stopOpacity="0.9" />
                  <stop offset="100%" stopColor="#020617" stopOpacity="0.85" />
                </linearGradient>

                {/* мягкий свет вокруг кольца */}
                <radialGradient id="ql7-orbit-halo" cx="50%" cy="50%" r="65%">
                  <stop offset="0%"  stopColor="#9e03f8ff" stopOpacity="0.45" />
                  <stop offset="40%" stopColor="#facc15" stopOpacity="0.35" />
                  <stop offset="100%" stopColor="#020617" stopOpacity="0" />
                </radialGradient>

                {/* золотой градиент для букв */}
                <linearGradient id="ql7-orbit-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%"   stopColor="#ffe600ff" />

                </linearGradient>
              </defs>

              {/* вращаем всё кольцо: активный пункт в зените */}
              <g
                className="nav-orbit-ring-svg"
                style={{
                  transform: `rotate(${-angleStep * activeIndex}deg)`,
                  transformOrigin: '50% 50%',
                }}
              >
                {/* световое пятно под монетой */}
                <circle
                  cx={CX}
                  cy={CY}
                  r={R + 32}
                  fill="url(#ql7-orbit-halo)"
                />

                {/* ТЁМНЫЙ УЗКИЙ РИНГ — подложка под текст, чтобы читалось */}
                <circle
                  cx={CX}
                  cy={CY}
                  r={R + 7}
                  fill="none"
                  stroke="url(#ql7-orbit-band-grad)"
                  strokeWidth="60"
                  className="nav-orbit-band"
                />

                {/* сами пункты меню по дуге */}
                {items.map((item, index) => {
                  const active = isActive(item.href)
                  const arcId = arcPaths[index].id
                  const label = (item.label || '').toUpperCase()

                  return (
                    <g
                      key={item.href}
                      className={
                        'nav-orbit-arc' + (active ? ' nav-orbit-arc--active' : '')
                      }
                      onPointerDown={beginOrbitPointerPress}
                      onPointerUp={finishOrbitPointerPress}
                      onPointerCancel={finishOrbitPointerPress}
                      onClick={() => navigateOrbit(item.href)}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && !e.repeat) {
                          e.preventDefault()
                          beginOrbitKeyboardPress(index)
                          navigateOrbit(item.href)
                        }
                      }}
                      role="link"
                      tabIndex={0}
                      aria-label={item.label}
                    >
                      <path
                        className="nav-orbit-hit-path"
                        d={arcPaths[index].d}
                        fill="none"
                        stroke="rgba(255,255,255,0.001)"
                        strokeWidth={ORBIT_HIT_STROKE_VIEWBOX}
                        strokeLinecap="butt"
                        aria-hidden="true"
                      />
                      <text className="nav-orbit-arc-text">
                        <textPath
                          href={`#${arcId}`}
                          startOffset="50%"
                          textAnchor="middle"
                        >
                         
                          {label}
                        
                          
                        </textPath>
                      </text>
                    </g>
                  )
                })}
              </g>
            </svg>

            {/* центр орбиты: джойстик; всегда идеально по центру, без съезда на арабском */}
            <div className="nav-orbit-center">
              <Link
                href="/game"
                className="nav-game-btn nav-orbit-center-btn"
                aria-label={t('nav_game') || 'Game'}
              >
                <ViewportAnimatedImage
                  animatedSrc="/game/game.gif"
                  alt={t('nav_game') || 'Game'}
                  width={110}
                  height={110}
                  className="nav-game-icon"
                  loading="lazy"
                  decoding="async"
                />
              </Link>
            </div>
          </div>
        </div>

        {/* Auth + язык: всегда прямо под навигацией, по центру относительно орбиты */}
        <div className="nav-orbit-controls">
          <span id={TOPBAR_AUTH_PORTAL_ID} className="ql7-topbar-auth-slot" />
          <button
            type="button"
            className={`topbarMetaMarketBtn ${metaMarketGiftAlert ? 'hasMetaGiftAlert' : ''}`}
            onClick={openMetaMarketFromBrand}
            aria-label={t('metamarket_open_aria') || 'Meta Market'}
            title={t('metamarket_open') || 'Meta Market'}
          >
            <span className="sr-only">{t('metamarket_open') || 'Meta Market'}</span>
            <svg className="topbarMetaMarketSvg" viewBox="0 0 112 34" aria-hidden="true">
              <defs>
                <linearGradient id="topbar-market-text-fill" x1="14" y1="0" x2="98" y2="0">
                  <stop offset="0%" stopColor="#f8ffff" />
                  <stop offset="38%" stopColor="#8ff7ff" />
                  <stop offset="62%" stopColor="#f5dc83" />
                  <stop offset="100%" stopColor="#ffffff" />
                </linearGradient>
                <filter id="topbar-market-glow" x="-28%" y="-95%" width="156%" height="290%">
                  <feDropShadow dx="0" dy="0" stdDeviation="0.75" floodColor="#f8ffff" floodOpacity="1" />
                  <feDropShadow dx="0" dy="0" stdDeviation="1.8" floodColor="#22d3ee" floodOpacity="0.7" />
                  <feDropShadow dx="0" dy="0" stdDeviation="3.2" floodColor="#facc15" floodOpacity="0.22" />
                </filter>
              </defs>
              <rect className="topbarMetaPlate" x="7" y="5" width="98" height="24" rx="12" />
              <path className="topbarMetaCircuit topbarMetaCircuit--top" d="M11 10H25L31 7H46M66 7H82L88 10H101" />
              <path className="topbarMetaCircuit topbarMetaCircuit--bottom" d="M12 25H35L41 28H70L76 25H100" />
              <circle className="topbarMetaNode topbarMetaNode--a" cx="14" cy="17" r="1.35" />
              <circle className="topbarMetaNode topbarMetaNode--b" cx="98" cy="17" r="1.35" />
              <path className="topbarMetaScanner" d="M18 17H94" />
              <g className="topbarMetaWord topbarMetaWord--meta">
                <text className="topbarMetaWordText topbarMetaWordText--meta" x="56" y="22" textAnchor="middle">Meta</text>
              </g>
              <g className="topbarMetaFragments">
                <circle className="topbarMetaShard topbarMetaShard--1" cx="39" cy="14" r="0.9" />
                <circle className="topbarMetaShard topbarMetaShard--2" cx="46" cy="20" r="0.75" />
                <circle className="topbarMetaShard topbarMetaShard--3" cx="52" cy="13" r="0.85" />
                <circle className="topbarMetaShard topbarMetaShard--4" cx="59" cy="21" r="0.75" />
                <circle className="topbarMetaShard topbarMetaShard--5" cx="66" cy="15" r="0.9" />
                <circle className="topbarMetaShard topbarMetaShard--6" cx="73" cy="19" r="0.7" />
                <path className="topbarMetaShard topbarMetaShard--7" d="M43 16l3 -2" />
                <path className="topbarMetaShard topbarMetaShard--8" d="M63 18l4 2" />
              </g>
              <g className="topbarMetaWord topbarMetaWord--market">
                <text className="topbarMetaWordText topbarMetaWordText--market" x="56" y="22" textAnchor="middle">Market</text>
              </g>
              <path className="topbarMetaComet" d="M21 26H91" />
            </svg>
            {metaMarketGiftAlert && (
              <span className="topbarMetaGiftBell" aria-hidden="true">
                <svg className="topbarMetaGiftBellSvg" viewBox="0 0 48 48" focusable="false">
                  <defs>
                    <radialGradient id="topbar-meta-gift-bell-glow" cx="50%" cy="45%" r="60%">
                      <stop offset="0%" stopColor="#fff7ed" stopOpacity="1" />
                      <stop offset="38%" stopColor="#ff3b30" stopOpacity=".92" />
                      <stop offset="100%" stopColor="#7f1d1d" stopOpacity="0" />
                    </radialGradient>
                    <linearGradient id="topbar-meta-gift-bell-fill" x1="12" y1="8" x2="36" y2="40">
                      <stop offset="0%" stopColor="#fff7ed" />
                      <stop offset="28%" stopColor="#ff5a4f" />
                      <stop offset="68%" stopColor="#dc2626" />
                      <stop offset="100%" stopColor="#7f1d1d" />
                    </linearGradient>
                    <filter id="topbar-meta-gift-bell-shadow" x="-70%" y="-70%" width="240%" height="240%">
                      <feDropShadow dx="0" dy="0" stdDeviation="1.35" floodColor="#fecaca" floodOpacity="1" />
                      <feDropShadow dx="0" dy="0" stdDeviation="3.4" floodColor="#ef4444" floodOpacity=".76" />
                    </filter>
                  </defs>
                  <circle className="topbarMetaGiftBellAura" cx="24" cy="24" r="17" fill="url(#topbar-meta-gift-bell-glow)" />
                  <g className="topbarMetaGiftBellBody" filter="url(#topbar-meta-gift-bell-shadow)">
                    <path className="topbarMetaGiftBellStroke" d="M18.5 18.7c.55-4.1 2.95-6.85 5.5-6.85s4.95 2.75 5.5 6.85l.64 5.22c.2 1.66.82 3.08 1.86 4.25l1.2 1.36H14.8l1.2-1.36c1.04-1.17 1.66-2.59 1.86-4.25l.64-5.22Z" />
                    <path className="topbarMetaGiftBellStroke" d="M20.7 32.05c.62 1.82 1.76 2.94 3.3 2.94s2.68-1.12 3.3-2.94" />
                    <path className="topbarMetaGiftBellClapper" d="M22.1 29.45h3.8c-.15 1.16-.82 1.9-1.9 1.9s-1.75-.74-1.9-1.9Z" />
                    <path className="topbarMetaGiftBellSpark" d="M24 9.3v-3" />
                  </g>
                  <g className="topbarMetaGiftBellParticles">
                    <circle className="topbarMetaGiftBellParticle topbarMetaGiftBellParticle--1" cx="12" cy="16" r="1.35" />
                    <circle className="topbarMetaGiftBellParticle topbarMetaGiftBellParticle--2" cx="36" cy="15" r="1.2" />
                    <circle className="topbarMetaGiftBellParticle topbarMetaGiftBellParticle--3" cx="10" cy="28" r="1.05" />
                    <circle className="topbarMetaGiftBellParticle topbarMetaGiftBellParticle--4" cx="38" cy="29" r="1" />
                    <path className="topbarMetaGiftBellParticle topbarMetaGiftBellParticle--5" d="M8 21h4" />
                    <path className="topbarMetaGiftBellParticle topbarMetaGiftBellParticle--6" d="M36 22h4" />
                  </g>
                </svg>
              </span>
            )}
          </button>
          <LanguageSwitcher />
        </div>
      </nav>
    </header>
  )
}

export default function TopBar () {
  const hostRef = useRef(null)
  const visualRef = useRef(null)
  const parkTimerRef = useRef(0)
  const shrinkConfirmTimerRef = useRef(0)
  const shrinkCandidateRef = useRef(null)
  const geometryLockTimerRef = useRef(0)
  const geometryLockUntilRef = useRef(0)
  const lastObservedHeightRef = useRef(0)
  const reservedHeightRef = useRef(0)
  const renderVisualRef = useRef(true)
  const [renderVisual, setRenderVisual] = useState(true)
  const [reservedHeight, setReservedHeight] = useState(0)

  // Keep the original global logout authority alive even while the visual shell is parked.
  if (typeof window !== 'undefined') {
    window.__QL7_TOPBAR_LOGOUT_ONCE__ ||= (() => {
      const onLogout = () => {
        try { window.dispatchEvent(new Event('aiquota:flush')) } catch {}
        try { clearAuthClientState() } catch {}
        try { window.location.reload() } catch {}
      }
      window.addEventListener('auth:logout', onLogout)
      return true
    })()
  }

  const clearParkTimer = useCallback(() => {
    if (!parkTimerRef.current || typeof window === 'undefined') return
    window.clearTimeout(parkTimerRef.current)
    parkTimerRef.current = 0
  }, [])

  const clearShrinkConfirmation = useCallback(() => {
    if (shrinkConfirmTimerRef.current && typeof window !== 'undefined') {
      window.clearTimeout(shrinkConfirmTimerRef.current)
    }
    shrinkConfirmTimerRef.current = 0
    shrinkCandidateRef.current = null
  }, [])

  const clearGeometryLock = useCallback(() => {
    if (geometryLockTimerRef.current && typeof window !== 'undefined') {
      window.clearTimeout(geometryLockTimerRef.current)
    }
    geometryLockTimerRef.current = 0
    geometryLockUntilRef.current = 0
  }, [])

  const applyReservedHeight = useCallback((nextHeight) => {
    const stableHeight = Math.round(Number(nextHeight || 0) * 1000) / 1000
    if (!Number.isFinite(stableHeight) || stableHeight <= 0.5) return 0
    const previousHeight = Number(reservedHeightRef.current || 0)
    if (Math.abs(stableHeight - previousHeight) <= TOPBAR_HEIGHT_EPSILON_PX) return previousHeight
    reservedHeightRef.current = stableHeight
    setReservedHeight(stableHeight)
    return stableHeight
  }, [])

  const submitHeightCandidate = useCallback((nextHeight) => {
    const stableHeight = Math.round(Number(nextHeight || 0) * 1000) / 1000
    if (!Number.isFinite(stableHeight) || stableHeight <= 0.5) return 0
    lastObservedHeightRef.current = stableHeight

    const currentHeight = Number(reservedHeightRef.current || 0)

    if (currentHeight <= 0.5 || stableHeight > currentHeight + TOPBAR_HEIGHT_EPSILON_PX) {
      clearShrinkConfirmation()
      return applyReservedHeight(stableHeight)
    }

    if (Math.abs(stableHeight - currentHeight) <= TOPBAR_HEIGHT_EPSILON_PX) {
      clearShrinkConfirmation()
      return currentHeight
    }

    if (performance.now() < Number(geometryLockUntilRef.current || 0)) {
      clearShrinkConfirmation()
      return currentHeight
    }

    const previousCandidate = shrinkCandidateRef.current
    if (!previousCandidate || Math.abs(Number(previousCandidate.height || 0) - stableHeight) > TOPBAR_HEIGHT_EPSILON_PX) {
      clearShrinkConfirmation()
      const candidate = { height: stableHeight }
      shrinkCandidateRef.current = candidate
      shrinkConfirmTimerRef.current = window.setTimeout(() => {
        shrinkConfirmTimerRef.current = 0
        if (shrinkCandidateRef.current !== candidate) return
        shrinkCandidateRef.current = null
        applyReservedHeight(candidate.height)
      }, TOPBAR_SHRINK_CONFIRM_MS)
    }
    return currentHeight
  }, [applyReservedHeight, clearShrinkConfirmation])

  const mountVisual = useCallback(() => {
    clearParkTimer()
    if (renderVisualRef.current) return
    renderVisualRef.current = true
    clearGeometryLock()
    clearShrinkConfirmation()
    geometryLockUntilRef.current = performance.now() + TOPBAR_REMOUNT_GEOMETRY_LOCK_MS
    geometryLockTimerRef.current = window.setTimeout(() => {
      geometryLockTimerRef.current = 0
      geometryLockUntilRef.current = 0
      const latestObservedHeight = Number(lastObservedHeightRef.current || 0)
      if (latestObservedHeight > 0.5) submitHeightCandidate(latestObservedHeight)
    }, TOPBAR_REMOUNT_GEOMETRY_LOCK_MS)
    setRenderVisual(true)
  }, [clearGeometryLock, clearParkTimer, clearShrinkConfirmation, submitHeightCandidate])

  const schedulePark = useCallback(() => {
    if (typeof window === 'undefined' || parkTimerRef.current) return
    if (Number(reservedHeightRef.current || 0) <= 0.5) return
    parkTimerRef.current = window.setTimeout(() => {
      parkTimerRef.current = 0
      const host = hostRef.current
      if (!host || typeof host.getBoundingClientRect !== 'function') return
      const rect = host.getBoundingClientRect()
      const viewportHeight = Math.max(0, Number(window.innerHeight || document.documentElement?.clientHeight || 0))
      const stillFarAbove = rect.bottom < -TOPBAR_KEEP_MARGIN_PX
      const stillFarBelow = rect.top > viewportHeight + TOPBAR_KEEP_MARGIN_PX
      if (stillFarAbove || stillFarBelow) {
        renderVisualRef.current = false
        clearGeometryLock()
        clearShrinkConfirmation()
        setRenderVisual(false)
      }
    }, TOPBAR_PARK_DELAY_MS)
  }, [clearGeometryLock, clearShrinkConfirmation])

  useEffect(() => {
    if (!renderVisual || typeof window === 'undefined') return undefined
    let disposed = false
    let resizeObserver = null
    let rafId = 0
    const node = visualRef.current

    const measureFallback = () => {
      if (disposed) return
      const fallbackNode = visualRef.current
      if (!fallbackNode || typeof fallbackNode.getBoundingClientRect !== 'function') return
      submitHeightCandidate(fallbackNode.getBoundingClientRect().height)
    }

    if (typeof ResizeObserver === 'function' && node) {
      resizeObserver = new ResizeObserver((entries) => {
        if (disposed) return
        const entry = entries?.[0]
        if (!entry) return
        const borderBox = entry.borderBoxSize?.[0] ?? entry.borderBoxSize
        const observedHeight = Number(borderBox?.blockSize ?? entry.contentRect?.height ?? 0)
        submitHeightCandidate(observedHeight)
      })
      resizeObserver.observe(node)
    } else {
      rafId = window.requestAnimationFrame(measureFallback)
      window.addEventListener('resize', measureFallback, { passive: true })
      try {
        document.fonts?.ready?.then(() => {
          if (!disposed) window.requestAnimationFrame(measureFallback)
        })
      } catch {}
    }

    return () => {
      disposed = true
      if (rafId) window.cancelAnimationFrame(rafId)
      resizeObserver?.disconnect?.()
      window.removeEventListener('resize', measureFallback)
    }
  }, [renderVisual, submitHeightCandidate])

  useEffect(() => {
    const host = hostRef.current
    if (!host || typeof window === 'undefined' || typeof IntersectionObserver !== 'function') return undefined

    let near = true
    let withinKeepRange = true

    const nearObserver = new IntersectionObserver((entries) => {
      near = !!entries[0]?.isIntersecting
      if (near) {
        mountVisual()
      } else if (!withinKeepRange) {
        schedulePark()
      }
    }, {
      root: null,
      rootMargin: `${TOPBAR_PREWARM_MARGIN_PX}px 0px ${TOPBAR_PREWARM_MARGIN_PX}px 0px`,
      threshold: 0,
    })

    const keepObserver = new IntersectionObserver((entries) => {
      withinKeepRange = !!entries[0]?.isIntersecting
      if (withinKeepRange) {
        clearParkTimer()
        return
      }
      if (!near) schedulePark()
    }, {
      root: null,
      rootMargin: `${TOPBAR_KEEP_MARGIN_PX}px 0px ${TOPBAR_KEEP_MARGIN_PX}px 0px`,
      threshold: 0,
    })

    nearObserver.observe(host)
    keepObserver.observe(host)

    return () => {
      clearParkTimer()
      nearObserver.disconnect()
      keepObserver.disconnect()
    }
  }, [clearParkTimer, mountVisual, schedulePark])

  useEffect(() => () => {
    clearParkTimer()
    clearGeometryLock()
    clearShrinkConfirmation()
  }, [clearGeometryLock, clearParkTimer, clearShrinkConfirmation])

  const hostStyle = reservedHeight > 0
    ? { height: `${reservedHeight}px` }
    : undefined

  return (
    <div
      ref={hostRef}
      className="ql7-topbar-host"
      style={hostStyle}
      data-ql7-topbar-state={renderVisual ? 'mounted' : 'parked'}
      data-ql7-topbar-reserved-height={reservedHeight > 0 ? String(reservedHeight) : 'measuring'}
    >
      <AuthNavClient
        portalTargetId={TOPBAR_AUTH_PORTAL_ID}
        portalChangeEvent={TOPBAR_PORTAL_EVENT}
      />
      {renderVisual ? <TopBarVisual rootRef={visualRef} /> : null}
    </div>
  )
}
